import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/shadcn/dialog";
import { Button } from "@/components/ui/shadcn/button";
import { Input } from "@/components/ui/shadcn/input";
import { SearchableDropdown } from "@/components/ui/searchable-dropdown";
import {
  ArrowRight,
  Pencil,
  CheckIcon,
  AlertCircle,
  PlusIcon,
} from "lucide-react";
import { quotesAPI, clientsAPI, vmarketsAPI } from "@/services/api";
import { Quote, QuoteCreateRequest, QuoteUnit, QuoteSide } from "@/types/quotes";
import { VirtualMarketResponseSchema } from "@/types/vmarkets";
import { DateTime } from "luxon";
import { useToast } from "@/hooks/use-toast";
import { handleApiError, showSuccessToast } from "@/lib/error-handler";

const QUOTE_EXPIRATION_MINUTES =
  Number(import.meta.env.VITE_QUOTE_EXPIRATION_MINUTES) || 15;

function useQuoteForm() {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    clientId: "",
    vmarketId: "",
    amount: "",
    unit: QuoteUnit.BASE,
    feesBps: "",
    expirationMinutes: QUOTE_EXPIRATION_MINUTES.toString(),
    side: QuoteSide.BUY,
  });

  const [clients, setClients] = useState<
    Array<{
      id: string;
      name: string;
      email: string;
      fees: number;
    }>
  >([]);

  const [vmarkets, setVmarkets] = useState<VirtualMarketResponseSchema[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFeeEditable, setIsFeeEditable] = useState(false);
  const [isExpirationEditable, setIsExpirationEditable] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [clientsData, vmarketsData] = await Promise.all([
          clientsAPI.getClients(),
          vmarketsAPI.getVirtualMarkets(),
        ]);
        setClients(clientsData);
        setVmarkets(vmarketsData);

        if (import.meta.env.DEV) {
          console.log("Quote form: Data fetched successfully:", {
            clients: clientsData.length,
            vmarkets: vmarketsData.length,
          });
        }
      } catch (err) {
        handleApiError("Quote Form - Fetch Data", err, toast);
        setError("Failed to load data. Please refresh the page and try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [toast]);

  const handleClientSelection = (clientId: string) => {
    if (!clientId) {
      setFormData((prev) => ({
        ...prev,
        clientId: "",
        feesBps: "",
      }));
      setIsFeeEditable(false);
      return;
    }

    const selectedClient = clients.find((client) => client.id === clientId);

    if (selectedClient) {
      setFormData((prev) => ({
        ...prev,
        clientId,
        feesBps: selectedClient.fees.toString(),
        expirationMinutes: QUOTE_EXPIRATION_MINUTES.toString(),
      }));
      setIsFeeEditable(false);
      setIsExpirationEditable(false);
    } else if (clientId) {
      console.warn("Selected client not found in clients array!");
      toast({
        title: "Warning",
        description: "Unable to load client details. Please select again.",
        variant: "warning",
      });
    }
  };

  const handleVmarketSelection = (vmarketId: string) => {
    if (!vmarketId) {
      setFormData((prev) => ({
        ...prev,
        vmarketId: "",
      }));
      return;
    }

    const selectedVmarket = vmarkets.find(
      (vmarket) => vmarket.id.toString() === vmarketId
    );

    if (selectedVmarket) {
      setFormData((prev) => ({
        ...prev,
        vmarketId: selectedVmarket.id.toString(),
      }));
    } else if (vmarketId) {
      console.warn("Selected virtual market not found!");
      toast({
        title: "Warning",
        description: "Unable to load virtual market details. Please select again.",
        variant: "warning",
      });
    }
  };

  const handleFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateField("feesBps", e.target.value);
  };

  const handleExpirationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateField("expirationMinutes", e.target.value);
  };

  const handleUnitChange = (value: QuoteUnit) => {
    setFormData((prev) => ({
      ...prev,
      unit: value,
    }));
  };

  const handleSideChange = (value: QuoteSide) => {
    setFormData((prev) => ({
      ...prev,
      side: value,
    }));
  };

  const updateField = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (error) setError(null);
  };

  return {
    formData,
    setFormData,
    clients,
    vmarkets,
    loading,
    setLoading,
    error,
    setError,
    isFeeEditable,
    setIsFeeEditable,
    isExpirationEditable,
    setIsExpirationEditable,
    handleClientSelection,
    handleVmarketSelection,
    handleUnitChange,
    handleFeeChange,
    handleExpirationChange,
    handleSideChange,
    toast,
    updateField,
  };
}

interface CreateQuoteDialogProps {
  onQuoteCreated: (quote: Quote) => void;
}

export function CreateQuoteDialog({ onQuoteCreated }: CreateQuoteDialogProps) {
  const [open, setOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const {
    formData,
    setFormData,
    clients,
    vmarkets,
    loading,
    setLoading,
    error,
    setError,
    isFeeEditable,
    setIsFeeEditable,
    isExpirationEditable,
    setIsExpirationEditable,
    handleClientSelection,
    handleVmarketSelection,
    handleUnitChange,
    handleFeeChange,
    handleExpirationChange,
    handleSideChange,
    toast,
    updateField,
  } = useQuoteForm();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formRef.current?.checkValidity()) {
      toast({
        title: "Validation Error",
        description: "Please fill all required fields correctly.",
        variant: "error",
      });
      return;
    }

    if (
      !formData.clientId ||
      !formData.vmarketId ||
      !formData.amount ||
      !formData.unit
    ) {
      setError("Please fill all required fields.");
      toast({
        title: "Validation Error",
        description:
          "Please select a client, virtual market, and enter an amount.",
        variant: "error",
      });
      return;
    }

    setLoading(true);
    setError(null);

    const expirationTime = DateTime.utc()
      .plus({ minutes: Number(formData.expirationMinutes) })
      .toISO();

    if (!expirationTime) {
      setError("Invalid expiration time");
      setLoading(false);
      return;
    }

    if (import.meta.env.DEV) {
      console.log("Form data: ", formData);
    }

    const quoteData: QuoteCreateRequest = {
      vmarket_id: Number(formData.vmarketId),
      amount: formData.amount,
      unit: formData.unit.toLowerCase() as QuoteUnit,
      fees_bps: formData.feesBps,
      client_id: formData.clientId,
      expiration: expirationTime,
      side: formData.side.toLowerCase() as QuoteSide,
    };

    try {
      const newQuote = await quotesAPI.createQuote(quoteData);
      showSuccessToast(toast, "Success", "Quote created successfully!");

      setIsFeeEditable(false);
      setIsExpirationEditable(false);

      onQuoteCreated(newQuote);
      setOpen(false);

      if (import.meta.env.DEV) {
        console.log("Quote created successfully:", newQuote);
      }
    } catch (err) {
      handleApiError("Create Quote", err, toast);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-black hover:bg-gray-800 text-white font-semibold transition-all duration-200 gap-2 border-white">
          <PlusIcon className="h-4 w-4" />
          New Quote
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Quote</DialogTitle>
          <DialogDescription>
            Fill in the details to create a new quote.
          </DialogDescription>
        </DialogHeader>
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-center space-x-2 bg-red-50 border border-red-200 rounded-md p-3">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <p className="text-red-600 text-sm font-medium">{error}</p>
            </div>
          )}

          {/* Client Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Client</label>
            <SearchableDropdown
              id="client-select"
              options={clients.map((client) => ({
                value: client.id,
                label: client.name,
              }))}
              value={formData.clientId}
              onChange={handleClientSelection}
              placeholder="Select a client"
              required
            />
          </div>

          {/* Virtual Market Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Virtual Market</label>
            <SearchableDropdown
              id="vmarket-select"
              options={vmarkets.map((vmarket) => ({
                value: vmarket.id.toString(),
                label: `(${vmarket.base}/${vmarket.counter}) ${vmarket.description}`,
              }))}
              value={formData.vmarketId}
              onChange={handleVmarketSelection}
              placeholder="Select a virtual market"
              required
            />
          </div>

          {/* Side Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Side</label>
            <SearchableDropdown
              id="side-select"
              options={[
                { value: QuoteSide.BUY, label: "Buy" },
                { value: QuoteSide.SELL, label: "Sell" },
              ]}
              value={formData.side}
              onChange={(value: string) => handleSideChange(value as QuoteSide)}
              placeholder="Select side"
              required
            />
          </div>

          {/* Amount and Unit */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label
                htmlFor="amount-input"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Amount
              </label>
              <Input
                id="amount-input"
                type="number"
                step="any"
                min="0"
                value={formData.amount}
                onChange={(e) => updateField("amount", e.target.value)}
                placeholder="0.00"
                className="border-gray-300 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-blue-500 focus-visible:border-2"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Unit</label>
              <SearchableDropdown
                id="unit-select"
                value={formData.unit}
                onChange={(value: string) => handleUnitChange(value as QuoteUnit)}
                options={(() => {
                  const selectedVmarket = vmarkets.find(v => v.id.toString() === formData.vmarketId);
                  if (!selectedVmarket) return [];
                  return [
                    { value: QuoteUnit.BASE, label: selectedVmarket.base },
                    { value: QuoteUnit.COUNTER, label: selectedVmarket.counter },
                  ];
                })()}
                placeholder="Select unit"
                required
              />
            </div>
          </div>

          {/* Fee */}
          <div className="space-y-2">
            <label
              htmlFor="fee-input"
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Fee (bps)
            </label>
            <div className="flex items-center gap-2">
              <Input
                id="fee-input"
                type="number"
                min="0"
                value={formData.feesBps}
                onChange={handleFeeChange}
                placeholder="Enter fee in basis points"
                className="border-gray-300 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-blue-500 focus-visible:border-2"
                disabled={!isFeeEditable}
                required
              />
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsFeeEditable(!isFeeEditable)}
                aria-label={isFeeEditable ? "Lock fee" : "Edit fee"}
              >
                {!isFeeEditable ? (
                  <Pencil className="h-4 w-4 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-blue-500 focus-visible:border-2" />
                ) : (
                  <CheckIcon className="h-4 w-4 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-blue-500 focus-visible:border-2" />
                )}
              </Button>
            </div>
          </div>

          {/* Expiration time */}
          <div className="space-y-2">
            <label
              htmlFor="expiration-input"
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Quote expiration time (minutes)
            </label>
            <div className="flex items-center gap-2">
              <Input
                id="expiration-input"
                type="number"
                min="0"
                value={formData.expirationMinutes}
                onChange={handleExpirationChange}
                placeholder="Enter the expiration time in minutes"
                className="border-gray-300 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-blue-500 focus-visible:border-2"
                disabled={!isExpirationEditable}
                required
              />
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsExpirationEditable(!isExpirationEditable)}
                aria-label={
                  isExpirationEditable ? "Lock expiration" : "Edit expiration"
                }
              >
                {!isExpirationEditable ? (
                  <Pencil className="h-4 w-4 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-blue-500 focus-visible:border-2" />
                ) : (
                  <CheckIcon className="h-4 w-4 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-blue-500 focus-visible:border-2" />
                )}
              </Button>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full mt-6 bg-black hover:bg-gray-800 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200"
            disabled={loading}
          >
            {loading ? "Processing..." : "Generate Quote"}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
